import { User } from "@fairfleet/geotab";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { isArchived } from "../usersPageHelpers";
import { ShowMoreButton } from "@/components/ShowMoreButton";
import { LIST_PAGE_SIZE } from "@/constants/renderLimits";
import { useShowMore } from "@/hooks/useShowMore";
import { getUserDisplayName } from "@/utilities/user/getUserDisplayName";

interface Props {
  users: User[];
  selectedUserId: string | null;
  /** Pass a stable callback: the list is memoized so typing in the form does not re-render it. */
  onSelect: (userId: string) => void;
}

interface RowProps {
  user: User;
  selected: boolean;
  onSelect: (userId: string) => void;
}

/** One row; memoized so selecting a user re-renders only the two rows that change. */
const UserRow = memo(function UserRow({ user, selected, onSelect }: RowProps) {
  const { t } = useTranslation();
  const archived = isArchived(user);
  return (
    <ListItemButton selected={selected} onClick={() => onSelect(user.id)}>
      <ListItemText
        primary={getUserDisplayName(user)}
        slotProps={{ secondary: { component: "div" } }}
        secondary={
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Typography variant="caption" sx={{ minWidth: 0, pr: 1 }} noWrap>
              {user.name}
            </Typography>
            <Chip
              size="small"
              label={archived ? t("users.statusArchived") : t("users.statusActive")}
              color={archived ? "default" : "success"}
            />
          </Stack>
        }
      />
    </ListItemButton>
  );
});

/**
 * Up to 5000 users, rendered {@link LIST_PAGE_SIZE} at a time; the list and each row re-render only
 * when their own data changes. Remount with a new `key` to start again at the first page.
 */
export const UserList = memo(function UserList({ users, selectedUserId, onSelect }: Props) {
  const { visible, hiddenCount, showMore } = useShowMore(users, LIST_PAGE_SIZE, selectedUserId);
  return (
    <>
      <List dense disablePadding>
        {visible.map(user => (
          <UserRow key={user.id} user={user} selected={selectedUserId === user.id} onSelect={onSelect} />
        ))}
      </List>
      <ShowMoreButton hiddenCount={hiddenCount} onClick={showMore} />
    </>
  );
});
